import React, { Component } from "react";
import PropTypes from "prop-types";
import { inject, observer } from "mobx-react";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";
import classnames from "classnames";
import Skeleton from "react-loading-skeleton";
import withCustomStyles from "./CategoriesPart.style";
import ImageWithLoaderPart from "./ImageWithLoaderPart";
import { lazyLoadComponentInViewPort } from "../utils/lazyLoadComponent";
import {
  getProductsPageUrl,
  getSiteMapProductsPageUrl
} from "../utils/UrlUtils";

const sectionId = category => `cat-${category.id}`;

// "All categories": one section per top-level category with a grid of
// subcategory tiles. `showNav` adds the sticky group list on the left
// (All categories page); Home shows the sections only.
@inject(["categoriesStore"])
@observer
class CategoriesPart extends Component {
  url = category =>
    this.props.isSiteMap
      ? getSiteMapProductsPageUrl(category)
      : getProductsPageUrl(category);

  renderSection(category) {
    const { classes, headingTag: H } = this.props;
    const subcats = category.subcat || [];

    const section = (
      <section
        id={sectionId(category)}
        className={classes.section}
        aria-labelledby={`${sectionId(category)}-title`}
      >
        <div className={classes.sectionHead}>
          <div className={classes.sectionTitles}>
            <H
              id={`${sectionId(category)}-title`}
              className={classes.sectionTitle}
            >
              {category.name}
            </H>
            {category.desc && (
              <span className={classes.sectionBlurb}>{category.desc}</span>
            )}
          </div>
          <Link to={this.url(category)} className={classes.seeAll}>
            See all
          </Link>
        </div>
        <div className={classes.grid}>
          {subcats.map(subcat => (
            <Link key={subcat.id} to={this.url(subcat)} className={classes.sub}>
              <span className={classes.subTile}>
                <ImageWithLoaderPart
                  width="132"
                  height="132"
                  src={subcat.image}
                  alt=""
                  className={classes.subImage}
                />
              </span>
              <span className={classes.subName}>{subcat.name}</span>
            </Link>
          ))}
        </div>
      </section>
    );

    return lazyLoadComponentInViewPort(section, {
      once: true,
      resize: true,
      offset: 400,
      height: 320,
      key: category.id
    });
  }

  renderLoading() {
    const { classes } = this.props;
    return Array.from({ length: 3 }, (_, i) => (
      <div key={i} className={classes.section} aria-hidden="true">
        <Skeleton width={220} height={28} />
        <div className={classes.grid}>
          {Array.from({ length: 6 }, (__, j) => (
            <Skeleton key={j} className={classes.skeletonTile} />
          ))}
        </div>
      </div>
    ));
  }

  render() {
    const { classes, categoriesStore, showNav, className } = this.props;
    const categories = categoriesStore.all.filter(
      category => category.subcat && category.subcat.length > 0
    );
    const loading =
      categoriesStore.all.length === 0 && categoriesStore.state === "fetching";

    return (
      <div className={classnames(classes.root, className)}>
        {showNav && categories.length > 0 && (
          <nav aria-label="Category groups" className={classes.nav}>
            {categories.map(category => (
              <a
                key={category.id}
                href={`#${sectionId(category)}`}
                className={classes.navItem}
              >
                {category.name}
                <span className={classes.navCount}>
                  {category.subcat.length}
                </span>
              </a>
            ))}
          </nav>
        )}
        <div className={classes.sections}>
          {loading
            ? this.renderLoading()
            : categories.map(category => this.renderSection(category))}
        </div>
      </div>
    );
  }
}

CategoriesPart.propTypes = {
  showNav: PropTypes.bool,
  isSiteMap: PropTypes.bool,
  headingTag: PropTypes.string
};

CategoriesPart.defaultProps = {
  showNav: false,
  headingTag: "h2"
};

export default withRouter(withCustomStyles(CategoriesPart));
